import React, { useState, useContext, useEffect } from "react";
import gql from "graphql-tag";
import { useQuery, useMutation } from "@apollo/react-hooks";
import { useSelector } from "react-redux";
import moment from "moment";
import "moment/locale/sv";
import { makeStyles } from "@material-ui/styles";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import { AppContext, OPEN_TOAST, CLOSE_BOOKINGSMODAL } from "../Context";
import { MY_BOOKINGS } from "../routes";
import Managing from "../Common/manages";
import Form from "./ParticipantForm";
import {
  STATE_ACTIVE_PERSON_ID,
  STATE_CULTURE,
  setActivePersonId,
} from "../Actions/stateActions";
import { ModalContext } from "../Context/ModalContext";
import { Modal } from "@mantine/core";
import { Typography } from "@material-ui/core";

const query = gql`
  query bookingModalQuery {
    whoami {
      subject
      name
      isAuthenticated
    }
    manages {
      id
      name
      email
      civicRegistrationNumber
      photo
    }
  }
`;

const BOOK_OCCASION = gql`
  mutation reserveOccasion($id: String, $participantId: Guid) {
    reserveOccasion(occasionId: $id, participantId: $participantId) {
      position
      failed
      errorMessage
    }
  }
`;

const BOOK_COURSE = gql`
  mutation reserveActivity($id: String!, $participantId: Guid) {
    reserveActivity(activityId: $id, participantId: $participantId) {
      position
      failed
      errorMessage
    }
  }
`;

const BOOK_COURSE_NEW = gql`
  mutation reserveActivity(
    $id: String
    $firstName: String
    $lastName: String
    $civicRegistrationNumber: String
    $participantIsForRegistration: Boolean
  ) {
    reserveActivity(
      activityId: $id
      participantFirstname: $firstName
      participantLastname: $lastName
      participantCivicRegistrationNumber: $civicRegistrationNumber
      participantIsForRegistration: $participantIsForRegistration
    ) {
      position
      failed
      errorMessage
    }
  }
`;

const useStyles = makeStyles((theme) => ({
  modalWrapper: {
    background: theme.app.background[1],
  },
  heading: {
    marginTop: "1.5rem",
    fontSize: "1.25rem !important",
    textAlign: "center",
    fontWeight: 500,
  },
  time: {
    fontSize: "1rem !important",
    fontWeight: "400 !important",
    textAlign: "center",
  },
  content: {
    color: theme.app.labelColor,
  },
  closeButton: {
    color: theme.app.labelColor,
  },
  noPadding: {
    padding: 0,
  },
}));

const getState = (state) => ({
  activePersonId: state.cookies[STATE_ACTIVE_PERSON_ID],
  culture: state.cookies[STATE_CULTURE],
  language: state.language,
});

const BookingModal = () => {
  const classes = useStyles();
  const context = useContext(AppContext);
  const { openModal, setOpenModal } = useContext(ModalContext);
  const { rdxDispatch, dispatch, state } = context;
  const { culture, language, activePersonId } = useSelector((state) =>
    getState(state)
  );
  const closeBookingsModal = () => dispatch({ type: CLOSE_BOOKINGSMODAL });
  const setActive = (id) => rdxDispatch(setActivePersonId(id));

  const bookingData = state.bookingModal.bookingData;
  const open = state.bookingModal.open;
  const [isNew, setIsNew] = useState(false);
  const handleMutationError = (error) => {
    dispatch({
      type: OPEN_TOAST,
      toastText: language.errorMsg,
      toastType: "error",
    });
    closeBookingsModal();
  };
  const handleMutationSuccess = ({ reserveOccasion, reserveActivity }) => {
    const data = reserveOccasion || reserveActivity;
    const { failed, errorMessage } = data;
    if (failed) {
      return dispatch({
        type: OPEN_TOAST,
        toastText: errorMessage.includes("already exists")
          ? language.reason.AlreadyExists
          : errorMessage,
        toastType: "error",
      });
    }

    if (bookingData?.activity?.price?.inclVat > 0) {
      setOpenModal(true);
    } else {
      dispatch({
        type: OPEN_TOAST,
        toastText: language.bookingSucceed,
        toastType: "success",
      });
    }
    closeBookingsModal();
  };
  const [bookCourseNew] = useMutation(BOOK_COURSE_NEW, {
    refetchQueries: () => [{ query: MY_BOOKINGS }],
    onCompleted: handleMutationSuccess,
    onError: handleMutationError,
  });

  const [bookCourse, { loading: loadBooking }] = useMutation(BOOK_COURSE, {
    variables: { id: bookingData.id, participantId: activePersonId },
    refetchQueries: () => [{ query: MY_BOOKINGS }],
    onCompleted: handleMutationSuccess,
    onError: handleMutationError,
  });

  const [bookOccasion] = useMutation(BOOK_OCCASION, {
    variables: { id: bookingData.id, participantId: activePersonId },
    refetchQueries: () => [{ query: MY_BOOKINGS }],
    onCompleted: handleMutationSuccess,
    onError: handleMutationError,
  });

  useEffect(() => {
    if (
      bookingData.canReserve &&
      !bookingData.canReserve.persons.includes(activePersonId)
    ) {
      rdxDispatch(setActivePersonId(undefined));
    }
  }, [bookingData, activePersonId, rdxDispatch]);
  const { data, loading, error } = useQuery(query);
  if (loading) return <div />;
  if (error) {
    console.log(error);
    return <div>Error</div>;
  }
  const { manages } = data;
  const activePerson =
    manages && activePersonId && manages.find((m) => m.id === activePersonId);
  const isOccasion = bookingData.__typename === "occasion";

  const locale = culture.substring(0, 2).toLowerCase();
  const when = isOccasion
    ? moment(bookingData.at).locale(locale).format("LLL")
    : `${moment(bookingData.firstOccasion).format("YYYY-MM-DD")} - ${moment(
        bookingData.lastOccasion
      ).format("YYYY-MM-DD")}`;
  const canBook = bookingData.canReserve && bookingData.canReserve.reserve;
  const canEnqueue = bookingData.canReserve && bookingData.canReserve.enqueue;

  let text = "";
  if ((canBook || canEnqueue) && language) {
    text = canBook ? (
      <span>
        {language.book} {activePerson && activePerson.name.split(" ")[0]}
      </span>
    ) : canEnqueue ? (
      <span>{language.enqueue}</span>
    ) : (
      language.reason[bookingData.canReserve.reason] ||
      bookingData.canReserve.reason
    );
  }

  return (
    <Modal
      opened={open}
      onClose={closeBookingsModal}
      closeButtonLabel={language.closeMenus}
      style={{ zIndex: 9998 }}
      classes={{ paper: classes.dialog }}
      size="lg"
      title={language.selectCustomer}
      centered
      classNames={{
        modal: classes.modalWrapper,
        title: classes.content,
        close: classes.closeButton,
        inner: classes.noPadding,
      }}
    >
      <main className={classes.content}>
        <Typography
          variant="h1"
          className={classes.heading}
          id="form-dialog-title"
        >
          {bookingData.activity ? bookingData.activity.name : ""}
        </Typography>
        <Typography variant="h2" className={classes.time}>
          {when}
        </Typography>

        <Managing
          active={activePersonId}
          bookingData={bookingData}
          handleSelect={(id) => setActive(id)}
          isNew={isNew}
          isOccasion={isOccasion}
          setIsNew={setIsNew}
          manages={manages}
        />
        {isNew && !isOccasion && (
          <Form
            handleSubmit={(v, a) => bookCourseNew({ variables: v })}
            id={bookingData.id}
            close={() => setIsNew(false)}
          />
        )}
        <DialogActions>
          {!isNew && (
            <Button
              color="secondary"
              onClick={closeBookingsModal}
            >{`${language.abort}`}</Button>
          )}
          {!isNew && (
            <Button
              disabled={!activePersonId || loadBooking}
              color="primary"
              onClick={isOccasion ? bookOccasion : bookCourse}
            >
              {text || "Boka"}
            </Button>
          )}
        </DialogActions>
      </main>
    </Modal>
  );
};

export default BookingModal;
